import React, { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import { 
  EyeIcon, 
  EyeOffIcon, 
  AlertCircleIcon, 
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon
} from "lucide-react"

interface SignupPageProps {
  logo?: string | React.ReactNode
  heading?: string
  subheading?: string
  signInHref?: string
  termsHref?: string
  onSignup?: (data: {
    fullName: string
    email: string
    password: string
  }) => Promise<void>
  showError?: boolean
  errorMessage?: string
  className?: string
  /** Devise form action URL (from ERB) */
  formAction?: string
  /** CSRF token (from ERB) */
  csrfToken?: string
  /** Devise login URL (from ERB) */
  loginUrl?: string
  /** Server-side errors (from ERB) */
  errors?: string[]
  /** Minimum password length from Devise config */
  minimumPasswordLength?: number
}

interface PasswordRequirement {
  label: string
  met: boolean
}

export function SignupPage({
  logo = "Academician Startup",
  heading = "Create your account",
  subheading = "Start your validation journey today",
  signInHref,
  termsHref = "/terms",
  onSignup,
  showError: externalShowError,
  errorMessage: externalErrorMessage,
  className,
  formAction,
  csrfToken,
  loginUrl,
  errors: serverErrors,
  minimumPasswordLength
}: SignupPageProps) {
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [designation, setDesignation] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  const formRef = useRef<HTMLFormElement>(null)

  // Resolve URLs: prefer ERB-provided URLs, fall back to defaults
  const resolvedSignInHref = loginUrl || signInHref || "/users/sign_in"

  // Field-level errors
  const [fullNameError, setFullNameError] = useState<string | null>(null)
  const [designationError, setDesignationError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null)
  const [termsError, setTermsError] = useState<string | null>(null)

  // Touched fields (for showing errors only after user interaction)
  const [touched, setTouched] = useState({
    fullName: false,
    designation: false,
    email: false,
    password: false,
    confirmPassword: false,
    terms: false
  })

  // Use server-side errors, external error, or internal error
  const serverError = serverErrors && serverErrors.length > 0 ? serverErrors.join(". ") : null
  const showError = externalShowError ?? !!(serverError || internalError)
  const errorMessage = externalErrorMessage ?? serverError ?? internalError

  // Password requirements (must match server-side validation in user.rb)
  const passwordRequirements: PasswordRequirement[] = [
    {
      label: "At least 8 characters",
      met: password.length >= 8
    },
    {
      label: "One lowercase letter",
      met: /[a-z]/.test(password)
    },
    {
      label: "One uppercase letter",
      met: /[A-Z]/.test(password)
    },
    {
      label: "One number",
      met: /[0-9]/.test(password)
    },
    {
      label: "One special character",
      met: /[^A-Za-z0-9]/.test(password)
    }
  ]

  const allRequirementsMet = passwordRequirements.every(req => req.met)

  // Password strength calculation
  const getPasswordStrength = (): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: "", color: "" }
    
    let strength = 0
    if (password.length >= 8) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    if (strength <= 2) {
      return { strength: 33, label: "Weak", color: "bg-destructive" }
    } else if (strength <= 3) {
      return { strength: 66, label: "Medium", color: "bg-yellow-500" }
    } else {
      return { strength: 100, label: "Strong", color: "bg-green-500" }
    }
  }

  const passwordStrength = getPasswordStrength()

  // Real-time validation
  useEffect(() => {
    if (touched.fullName) {
      if (!fullName.trim()) {
        setFullNameError("Full name is required")
      } else if (fullName.trim().length < 2) {
        setFullNameError("Name must be at least 2 characters")
      } else {
        setFullNameError(null)
      }
    }
  }, [fullName, touched.fullName])

  useEffect(() => {
    if (touched.designation) {
      if (!designation) {
        setDesignationError("Designation is required")
      } else {
        setDesignationError(null)
      }
    }
  }, [designation, touched.designation])

  useEffect(() => {
    if (touched.email) {
      if (!email) {
        setEmailError("Email is required")
      } else if (!email.includes("@") || !email.includes(".")) {
        setEmailError("Please enter a valid email address")
      } else {
        setEmailError(null)
      }
    }
  }, [email, touched.email])

  useEffect(() => {
    if (touched.password) {
      if (!password) {
        setPasswordError("Password is required")
      } else if (!allRequirementsMet) {
        setPasswordError("Password does not meet all requirements")
      } else {
        setPasswordError(null)
      }
    }
  }, [password, touched.password, allRequirementsMet])

  useEffect(() => {
    if (touched.confirmPassword) {
      if (!confirmPassword) {
        setConfirmPasswordError("Please confirm your password")
      } else if (password !== confirmPassword) {
        setConfirmPasswordError("Passwords do not match")
      } else {
        setConfirmPasswordError(null)
      }
    }
  }, [confirmPassword, password, touched.confirmPassword])

  useEffect(() => {
    if (touched.terms) {
      if (!agreeToTerms) {
        setTermsError("You must agree to the Terms of Service")
      } else {
        setTermsError(null)
      }
    }
  }, [agreeToTerms, touched.terms])

  const handleBlur = (field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInternalError(null)

    // Mark all fields as touched
    setTouched({
      fullName: true,
      designation: true,
      email: true,
      password: true,
      confirmPassword: true,
      terms: true
    })

    // Validate all fields
    const errors: string[] = []

    if (!fullName.trim()) {
      setFullNameError("Full name is required")
      errors.push("Full name is required")
    } else if (fullName.trim().length < 2) {
      setFullNameError("Name must be at least 2 characters")
      errors.push("Name must be at least 2 characters")
    }

    if (!designation) {
      setDesignationError("Designation is required")
      errors.push("Designation is required")
    }

    if (!email) {
      setEmailError("Email is required")
      errors.push("Email is required")
    } else if (!email.includes("@") || !email.includes(".")) {
      setEmailError("Please enter a valid email address")
      errors.push("Please enter a valid email address")
    }

    if (!password) {
      setPasswordError("Password is required")
      errors.push("Password is required")
    } else if (!allRequirementsMet) {
      setPasswordError("Password does not meet all requirements")
      errors.push("Password does not meet all requirements")
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password")
      errors.push("Please confirm your password")
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match")
      errors.push("Passwords do not match")
    }

    if (!agreeToTerms) {
      setTermsError("You must agree to the Terms of Service")
      errors.push("You must agree to the Terms of Service")
    }

    if (errors.length > 0) {
      setInternalError("Please fix the errors below")
      return
    }

    setIsLoading(true)

    // If formAction is provided (from Devise ERB), submit as a real form POST
    if (formAction) {
      formRef.current?.submit()
      return
    }

    try {
      if (onSignup) {
        await onSignup({ fullName: fullName.trim(), email, password })
      }
    } catch (error) {
      setInternalError(
        error instanceof Error ? error.message : "An error occurred during signup. Please try again."
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("min-h-screen bg-muted/30 flex items-center justify-center p-4", className)}>
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            {typeof logo === "string" ? (
              <h1 className="text-3xl font-bold text-[#666666]">{logo}</h1>
            ) : (
              logo
            )}
          </div>

          {/* Heading */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{heading}</h2>
            <p className="text-sm text-muted-foreground">{subheading}</p>
          </div>

          {/* Error Alert */}
          {showError && errorMessage && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
              <AlertCircleIcon className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} action={formAction} method="post" className="space-y-4">
            {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
            {/* Full Name Field */}
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="user[full_name]"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={() => handleBlur("fullName")}
                disabled={isLoading}
                className={cn(
                  fullNameError && touched.fullName && "border-destructive focus-visible:ring-destructive"
                )}
              />
              {fullNameError && touched.fullName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {fullNameError}
                </p>
              )}
            </div>

            {/* Designation Field */}
            <div className="space-y-2">
              <Label htmlFor="designation">Designation</Label>
              <select
                id="designation"
                name="user[designation]"
                value={designation}
                onChange={(e) => {
                  setDesignation(e.target.value)
                  if (!touched.designation) handleBlur("designation")
                }}
                onBlur={() => handleBlur("designation")}
                disabled={isLoading}
                className={cn(
                  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  !designation && "text-muted-foreground",
                  designationError && touched.designation && "border-destructive focus-visible:ring-destructive"
                )}
              >
                <option value="">Select your designation</option>
                <option value="ceo">CEO</option>
                <option value="cto">CTO</option>
                <option value="ct">CT</option>
                <option value="tt">TT</option>
              </select>
              <p className="text-xs text-muted-foreground italic">
                TT = Technical Team, CT = Commercial Team
              </p>
              {designationError && touched.designation && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {designationError}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="user[email]"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur("email")}
                disabled={isLoading}
                className={cn(
                  emailError && touched.email && "border-destructive focus-visible:ring-destructive"
                )}
              />
              {emailError && touched.email && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {emailError}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="user[password]"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => handleBlur("password")}
                  disabled={isLoading}
                  className={cn(
                    "pr-10",
                    passwordError && touched.password && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn("h-full transition-all duration-300", passwordStrength.color)}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>
              )}

              {/* Password Requirements */}
              <div className="space-y-1.5 pt-1">
                {passwordRequirements.map((req, index) => (
                  <div
                    key={index}
                    className={cn(
                      "text-xs flex items-center gap-1.5 transition-colors",
                      req.met ? "text-green-600 dark:text-green-500" : "text-muted-foreground"
                    )}
                  >
                    {req.met ? (
                      <CheckCircle2Icon className="w-3 h-3" />
                    ) : (
                      <XCircleIcon className="w-3 h-3" />
                    )}
                    {req.label}
                  </div>
                ))}
              </div>

              {passwordError && touched.password && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {passwordError}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="user[password_confirmation]"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur("confirmPassword")}
                  disabled={isLoading}
                  className={cn(
                    "pr-10",
                    confirmPasswordError && touched.confirmPassword && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
              {confirmPasswordError && touched.confirmPassword && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {confirmPasswordError}
                </p>
              )}
              {!confirmPasswordError && confirmPassword && password === confirmPassword && (
                <p className="text-xs text-green-600 dark:text-green-500 flex items-center gap-1">
                  <CheckCircle2Icon className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={agreeToTerms}
                  onCheckedChange={(checked) => {
                    setAgreeToTerms(checked === true)
                    handleBlur("terms")
                  }}
                  disabled={isLoading}
                  className={cn(
                    "mt-0.5",
                    termsError && touched.terms && "border-destructive"
                  )}
                />
                <Label
                  htmlFor="terms"
                  className="text-sm font-normal cursor-pointer leading-tight"
                >
                  I agree to the{" "}
                  <a
                    href={termsHref}
                    className="font-medium text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                </Label>
              </div>
              {termsError && touched.terms && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  {termsError}
                </p>
              )}
            </div>

            {/* Create Account Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <a
              href={resolvedSignInHref}
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </a>
          </div>
        </div>

        {/* Helper Text */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          By creating an account, you agree to our{" "}
          <a href="/terms" className="underline hover:text-foreground">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-foreground">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  )
}